import { useEffect, useRef, useState } from 'react';
import { Box, Button, FormControlLabel, Switch, TextField, Typography } from '@mui/material';

import { PERFORMANCE_ERROR } from '../../hooks/useMemberPerformanceProfile';
import { Card, Section } from './Card';
import { COLORS } from './constants';

const GENERIC_ERROR = 'Could not change sharing. Please try again.';

export const getShareLink = (username) =>
  `${window.location.origin}/member/${encodeURIComponent(username)}/performance`;

const Sharing = ({ sharing, username, onSetSharing, onSignIn, feedback, setFeedback }) => {
  const [saving, setSaving] = useState(false);
  // What the server confirmed, used when the follow-up refetch fails or lags behind.
  const [confirmed, setConfirmed] = useState(null);
  const [pending, setPending] = useState(null);
  const linkRef = useRef(null);

  const profileEnabled = sharing?.enabled === true;
  useEffect(() => {
    setConfirmed(null);
  }, [profileEnabled]);

  const enabled = pending !== null ? pending : confirmed !== null ? confirmed : profileEnabled;
  const link = getShareLink(username);

  const toggle = async (event) => {
    if (saving) {
      return;
    }
    const control = event.target;
    const next = event.target.checked;
    setFeedback(null);
    setPending(next);
    setSaving(true);
    try {
      const data = await onSetSharing(next);
      setConfirmed(typeof data?.sharing?.enabled === 'boolean' ? data.sharing.enabled : next);
      setFeedback({
        source: 'sharing',
        type: 'status',
        text: next
          ? 'Sharing is on. Other members can see your performance.'
          : 'Sharing is off. Only you can see your performance.',
      });
    } catch (err) {
      // The switch falls back to the saved value (pending is cleared below).
      if (err?.kind === PERFORMANCE_ERROR.SIGN_IN) {
        onSignIn?.();
      } else {
        setFeedback({
          source: 'sharing',
          type: 'error',
          text: err?.userMessage || GENERIC_ERROR,
        });
      }
    } finally {
      setPending(null);
      setSaving(false);
      // aria-disabled keeps focus; only restore it when focus was lost to the page body.
      if (control?.isConnected && document.activeElement === document.body) {
        control.focus();
      }
    }
  };

  const copy = async () => {
    setFeedback(null);
    try {
      await navigator.clipboard.writeText(link);
      setFeedback({ source: 'sharing', type: 'status', text: 'Link copied.' });
      return;
    } catch (err) {
      // Fall through to the manual fallback.
    }
    const input = linkRef.current;
    let copied = false;
    if (input) {
      input.focus();
      input.select();
      try {
        copied = Boolean(document.execCommand && document.execCommand('copy'));
      } catch (err) {
        copied = false;
      }
    }
    setFeedback({
      source: 'sharing',
      type: 'status',
      text: copied ? 'Link copied.' : 'The link is selected. Press Ctrl+C or Cmd+C to copy it.',
    });
  };

  return (
    <Section title="Sharing" id="perf-sharing">
      <Card>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={toggle}
              inputProps={{ 'aria-describedby': 'sharing-help', 'aria-disabled': saving }}
            />
          }
          label="Share my performance with other members"
        />
        {saving && (
          <Typography component="span" sx={{ ml: 1, fontSize: 14, opacity: 0.85 }}>
            Saving...
          </Typography>
        )}
        <Box id="sharing-help" sx={{ mt: 1, fontSize: 14 }}>
          <Typography sx={{ fontSize: 14, opacity: 0.9 }}>
            Sharing is off by default. When it is on, any signed-in member can open a read-only copy
            of this page by your username. The link is not a secret. You can turn it off at any
            time.
          </Typography>
          <Typography variant="h3" sx={{ mt: 2, fontSize: 15, fontWeight: 700 }}>
            Others can see
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            <li>Your trainer's recap</li>
            <li>Your lift ratings</li>
            <li>Your muscle and push/pull balance</li>
            <li>Your consistency and intensity</li>
            <li>Your weekly activity</li>
            <li>Your username, session counts and dates</li>
          </Box>
          <Typography variant="h3" sx={{ mt: 2, fontSize: 15, fontWeight: 700 }}>
            Others cannot see
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            <li>Your time away</li>
            <li>Your targets</li>
            <li>Your next session plan</li>
          </Box>
        </Box>
        {feedback?.source === 'sharing' && feedback.type === 'error' && (
          <Typography role="alert" sx={{ mt: 1, fontSize: 14, color: COLORS.caution }}>
            {feedback.text}
          </Typography>
        )}
        {feedback?.source === 'sharing' && feedback.type === 'status' && (
          <Typography data-testid="sharing-status" sx={{ mt: 1, fontSize: 14 }}>
            {feedback.text}
          </Typography>
        )}
        {enabled ? (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField
              label="Link to your page"
              size="small"
              value={link}
              inputRef={linkRef}
              inputProps={{ readOnly: true }}
              onFocus={(event) => event.target.select()}
              sx={{ flex: '1 1 260px' }}
            />
            <Button variant="outlined" onClick={copy}>
              Copy link
            </Button>
          </Box>
        ) : (
          <Typography sx={{ mt: 2, fontSize: 13, opacity: 0.8 }}>
            Turn sharing on to get a link you can send.
          </Typography>
        )}
      </Card>
    </Section>
  );
};

export default Sharing;
